'use client'

import { Login, PageMode } from '@moment/auth-ui'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useCallback, useState } from 'react'

import { resolvePostLoginRedirect } from '@/app/auth/_lib/helpers'
import { useAuthApi } from '@/app/auth/_lib/use-auth-api'
import LoginPageHeader from '@/app/login/header'
import {
  FormContainer,
  LoginPageContainer,
  LoginPageContent,
  PrivacyPolicy,
} from '@/app/login/styles'
import { Routes } from '@/app/routes'
import Logo from '@/components/atomic/logo'
import AnonymousStatsigProvider from '@/components/config/statsig-provider/anonymous-provider'
import { cn } from '@/utils/shadcn/utils'

import BackgroundLines from './illustration'

const LoginContent: React.FC = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const {
    passwordAuth,
    verifyMagicAuth,
    sendPasswordReset,
    resetPassword,
    getSsoConnections,
    getSsoAuthorizationUrl,
  } = useAuthApi()

  const resetToken = searchParams.get('token') ?? undefined
  const resetEmail = searchParams.get('email') ?? undefined
  const returnUrl = searchParams.get('returnUrl') ?? undefined

  const [currentMode, setCurrentMode] = useState<PageMode>(PageMode.Login)
  const [showResetSuccess, setShowResetSuccess] = useState(false)

  const handleModeChange = useCallback((mode: PageMode) => {
    // Clear success message when starting any reset flow
    if (
      mode === PageMode.ResetRequest ||
      mode === PageMode.ResetEmailSent ||
      mode === PageMode.ResetPassword
    ) {
      setShowResetSuccess(false)
    }

    setCurrentMode(mode)
  }, [])

  const handleResetSuccess = useCallback(() => {
    setShowResetSuccess(true)
    setCurrentMode(PageMode.Login)
  }, [])

  const handleLoginSuccess = useCallback(
    (redirectTo?: string) => {
      // A backend-supplied redirectTo is trusted and used verbatim; otherwise
      // fall back to the sanitized returnUrl param. Force full page reload to
      // fetch fresh HTML with updated CSP headers (router nav keeps login CSP).
      window.location.href = redirectTo || resolvePostLoginRedirect(returnUrl)
    },
    [returnUrl],
  )

  const handleSsoRedirect = useCallback((url: string) => {
    // Force full page reload to fetch fresh HTML with updated CSP headers.
    window.location.href = url
  }, [])

  const handleClearResetToken = useCallback(() => {
    router.replace('/login')
  }, [router])

  return (
    <AnonymousStatsigProvider>
      <LoginPageContainer className="login-page">
        <BackgroundLines
          className={cn(
            'absolute right-0 top-0 scale-x-[-1]',
            'text-(--color-border-secondary) dark:text-[#2E2E2E]',
            'pointer-events-none',
            'hidden xl:block',
          )}
        />

        <LoginPageContent>
          <Logo variant="both" className="w-23.5" />
          <FormContainer>
            <LoginPageHeader
              currentMode={currentMode}
              onModeChange={handleModeChange}
              showResetSuccess={showResetSuccess}
            />

            <Login
              onPasswordAuth={passwordAuth}
              onVerifyMagicAuth={verifyMagicAuth}
              onSendPasswordReset={sendPasswordReset}
              onResetPassword={resetPassword}
              onGetSsoConnections={getSsoConnections}
              onGetSsoAuthorizationUrl={getSsoAuthorizationUrl}
              onLoginSuccess={handleLoginSuccess}
              onSsoRedirect={handleSsoRedirect}
              onClearResetToken={handleClearResetToken}
              onModeChange={handleModeChange}
              onResetSuccess={handleResetSuccess}
              showResetSuccess={showResetSuccess}
              resetToken={resetToken}
              resetEmail={resetEmail}
              returnUrl={returnUrl}
              ssoCallbackUrl={Routes.Auth.callback}
            />
          </FormContainer>
          <PrivacyPolicy />
        </LoginPageContent>

        <BackgroundLines
          className={cn(
            'absolute left-0 bottom-0 scale-y-[-1]',
            'text-(--color-border-secondary) dark:text-[#2E2E2E]',
            'pointer-events-none',
            'hidden xl:block',
          )}
        />
      </LoginPageContainer>
    </AnonymousStatsigProvider>
  )
}

const LoginPage: React.FC = () => (
  <Suspense>
    <LoginContent />
  </Suspense>
)

export default LoginPage
